"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CountryTestResults = void 0;
const countries = require('./../../static/data/countries.json').countries;
const DistanceCalculator_1 = require("./DistanceCalculator");
class CountryTestResults {
    /**
     * Based on the questions that are passed along, which have also the answers
     * inside, retrieve the test results.
     *
     * @param {IQuestion[]} questions
     * @returns {IResult[]}
     */
    static getResults(questions) {
        let results = new Array();
        countries.forEach(country => {
            // Currently not all countries can be matched with the student, only those with bonuses.
            if (!country.bonuses) {
                return;
            }
            const result = CountryTestResults.getResultsForCountry(country, questions);
            results.push(result);
        });
        results = CountryTestResults.sortResults(results);
        return results;
    }
    /**
     * Sort the country results based on the bonus that was given to each country.
     *
     * @param {IResult[]} results
     * @returns {IResult[]}
     */
    static sortResults(results) {
        return results.sort((a, b) => (a.bonus > b.bonus) ? -1 : 1);
    }
    /**
     * Retrieve the set of properties that are needed to understand the test
     * results for a requested country.
     *
     * @param {ICountry} country
     * @param {IQuestion[]} questions
     * @returns {IResult}
     */
    static getResultsForCountry(country, questions) {
        let countryResult = {
            id: country.id,
            name: country.name,
            iso: country.iso,
            urlName: country.urlName,
            description: country.description,
            bonus: 0
        };
        questions.forEach(question => {
            if (!CountryTestResults.isQuestionRegular(question.type)) {
                return;
            }
            countryResult.bonus += CountryTestResults.getRegularResult(country, question);
        });
        countryResult.bonus += CountryTestResults.getDistanceResult(country, questions);
        return countryResult;
    }
    /**
     * Verify whether the question that is requested is of the same format as most
     * of the questions. All the questions that have this same format can be analysed
     * in the same way.
     *
     * @param {string} type
     * @returns {boolean}
     */
    static isQuestionRegular(type) {
        const regularTypes = ['whichDiscipline', 'englishFluency', 'activity', 'howInternational', 'localLanguageDifficulty', 'budget'];
        return regularTypes.includes(type);
    }
    /**
     * Determine the bonus that a certain country gets for a certain question,
     * provided that the question is regular (of similar format as most questions
     * and can therefore be analysed in a similar way).
     *
     * @param {ICountry} country
     * @param {IQuestion} question
     * @returns {number}
     */
    static getRegularResult(country, question) {
        const bonusDetails = country.bonuses[question.type].find(bonus => bonus.id === question.answer);
        return bonusDetails && bonusDetails.bonus ? bonusDetails.bonus : 0;
    }
    /**
     * Determine the bonus that a certain country gets based on [1] the distance from
     * the user's country of origin and [2] the user's distance preferences.
     *
     * @param {ICountry} country
     * @param {IQuestion[]} questions
     * @returns {number}
     */
    static getDistanceResult(country, questions) {
        const distance = CountryTestResults.getDistance(country, questions);
        if (typeof distance !== 'number') {
            return 0;
        }
        const howFarQuestion = questions.find(question => question.type === 'howFarAway');
        if (!howFarQuestion || !howFarQuestion.answer) {
            return 0;
        }
        const bonusData = {
            close: [
                { bonus: 2, min: 1000, max: 5000 },
                { bonus: 6, max: 1000 }
            ],
            further: [
                { bonus: 6, min: 1000, max: 5000 },
                { bonus: 4, max: 1000 }
            ],
            world: [
                { bonus: 6, min: 5000 },
                { bonus: 2, min: 1000, max: 5000 }
            ]
        };
        const bonusDetails = bonusData[howFarQuestion.answer].find(details => (!details.min || details.min <= distance) && (!details.max || details.max > distance));
        return bonusDetails && bonusDetails.bonus ? bonusDetails.bonus : 0;
    }
    /**
     * Retrieve the distance between a given country and the user's country of origin.
     *
     * @param {ICountry} country
     * @param {IQuestion[]} questions
     * @returns {number | null}
     */
    static getDistance(country, questions) {
        const originQuestion = questions.find(question => question.type === 'yourOrigin');
        if (!originQuestion || !originQuestion.answer) {
            return null;
        }
        const countryOfOrigin = countries.find(originCountry => originCountry.iso === originQuestion.answer);
        const originLocation = { lat: countryOfOrigin.lat, lon: countryOfOrigin.lon };
        const comparedLocation = { lat: country.lat, lon: country.lon };
        return DistanceCalculator_1.DistanceCalculator.calculateDistance(originLocation, comparedLocation);
    }
}
exports.CountryTestResults = CountryTestResults;
